import { Search } from "lucide-react";
import { useContext, useEffect, useState } from "react";
import { useCookies } from "react-cookie";
import { useNavigate } from "react-router-dom";

import { Button } from "@/app/components/ui/button";
import { Input } from "@/app/components/ui/input";
import type { ResumeContextValue } from "@/app/types/resume";

import tips from "../../../../assets/copy/tips";
import ErrorToast from "../../../../components/ErrorToast";
import EditorListItems from "../../../../components/ResumeBuilder/EditorListItems";
import type { EditorListItem } from "../../../../components/ResumeBuilder/EditorListItems/EditorListItems";
import Spinner from "../../../../components/Spinner";
import TextEditor from "../../../../components/TextEditor";
import Tips from "../../../../components/Tips";
import ResumeContext from "../../../../context/ResumeContext";
import { Certification } from "../../../../models/resume/certification";
import { StaticValue } from "../../../../seed/constants";
import { usePatchResumeSection } from "../../../../services/create-resume/queries";
import { RESUME_SECTIONS } from "../../../../services/create-resume/resumeSections";
import certificationListSuggestions from "./certificationList";

interface PatchError {
	response: { data: { message: string | string[] } };
}

const Certifications = () => {
	const [cookies] = useCookies(["resume"]);
	const navigate = useNavigate();

	const staticValue = new StaticValue();
	const patchSection = usePatchResumeSection();
	const staticValues = staticValue.getDefaultValues();

	const [serverErrors, setServerErrors] = useState<string[]>([]);
	const [certificationList, setCertificationList] = useState<EditorListItem[]>([...certificationListSuggestions]);

	//Declaring the certifications state
	const { resumeData, setResumeData } = useContext(ResumeContext) as ResumeContextValue;

	const [certifications, setCertifications] = useState(resumeData.extras.certifications);

	const [userInput, setUserInput] = useState(resumeData.contactInfo.resumeTitle);

	useEffect(() => {
		setCertifications(resumeData.extras.certifications);
	}, [resumeData]);

	useEffect(() => {
		setUserInput(resumeData.contactInfo.resumeTitle);
	}, [resumeData]);

	//submition handler
	const addCertifications = () => {
		setResumeData((prevState) => {
			return {
				...prevState,
				extras: {
					...prevState.extras,
					certifications: certifications,
				},
			};
		});

		const payload = new Certification(certifications);
		const request = payload.getCertificationRequest();

		const resumeId = cookies[staticValues.localStorageResumeId];

		patchSection.mutate({ resumeId, section: RESUME_SECTIONS.CERTIFICATION, data: request } as never, {
			onSuccess,
			onError,
		});
	};

	function onSuccess() {
		navigate("/resume/extras");
	}

	function onError(error: Error) {
		const serverResponse = (error as unknown as PatchError).response.data;
		Array.isArray(serverResponse.message)
			? setServerErrors((prev) => {
					return [...prev, ...serverResponse.message];
				})
			: setServerErrors((prev) => {
					return [...prev, serverResponse.message as string];
				});
	}

	return (
		<div className="min-h-screen">
			{serverErrors.map((item, index) => {
				return <ErrorToast error={item} key={index} />;
			})}
			<div className="grid grid-cols-1 gap-6 md:grid-cols-2">
				<div>
					<div className="mb-4">
						<div className="flex justify-between gap-2">
							<p className="mb-3 text-gray-700">Add details about your professional certifications.</p>
							<Tips content={tips.certifications} />
						</div>
						<div className="flex">
							<Input type="text" className="rounded-r-none" placeholder={userInput} />
							<Button type="button" className="rounded-l-none md:px-10" aria-label="Search">
								<Search className="size-4" />
							</Button>
						</div>
					</div>
					<EditorListItems
						editorListItems={certificationList}
						editorContent={certifications}
						setEditorContent={setCertifications}
						setEditorListItems={setCertificationList}
					/>
				</div>
				<div className="mt-4 flex flex-col justify-end">
					<TextEditor
						value={certifications}
						setValue={setCertifications}
						placeholder="Add your certifications here..."
					/>
				</div>
				<div className="col-span-full mt-4 flex items-center justify-end gap-2">
					<Button
						type="button"
						variant="secondary"
						className="rounded-full uppercase"
						size="lg"
						onClick={() => {
							navigate("/resume/extras");
						}}
					>
						Cancel
					</Button>
					<Button type="button" onClick={addCertifications} className="uppercase">
						{patchSection.isPending ? (
							<>
								Loading <Spinner />
							</>
						) : (
							"Save"
						)}
					</Button>
				</div>
			</div>
		</div>
	);
};

export default Certifications;
